import { useCallback, useState } from "react";

/**
 * Which containers a picker has selected.
 *
 * Both pickers select the same way — a row at a time, or a whole waste stream
 * at once from its heading — so they share the state rather than each keeping
 * its own copy of the same two updaters.
 *
 * `initialIds` pre-selects containers the caller already chose elsewhere, so
 * arriving from a container's own drawer doesn't mean finding it again in the
 * list.
 */
export function useContainerSelection(initialIds?: readonly string[]) {
  // Seeded once, deliberately: `initialIds` comes from the URL a caller
  // arrived on (e.g. Request manifest from a container drawer), and it is a
  // starting point rather than a controlled value — re-syncing it would undo
  // the user's own picks every time the query string is rewritten.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialIds ?? []));

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Add or remove a batch of ids at once — the master and per-stream checkboxes
  // select or clear their whole scope through this.
  const setSelection = useCallback((ids: string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }, []);

  /** Start over — e.g. the picker switched to a different facility. */
  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return { selectedIds, toggle, setSelection, clear };
}
